import { Accordion, Button, Icon } from 'platfi-ui-components';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { getUiSchema } from 'ui-schemas';
import {
  makeSelectRequestHelpResponse,
  makeSelectRequestHelpLoading,
} from 'reducers/requestHelp/selectors';
import { Map } from 'immutable';
import { requestRequestHelp, requestRequestHelpUpdate } from 'reducers/requestHelp/reducer';
import CSSModules from 'react-css-modules';
import FormBuilder from 'components/formBuilder';
import FullPageSpinner from 'modules/FullPageSpinner/FullPageSpinner';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import withModal from 'HOC/withModal';
import withNotify from 'HOC/withNotify';

import styles from './RequestHelpModal.scss';

const defaultInitialValues = (clientName) => ({
  subject: `Help ${clientName} complete your Funding Search on Capitalise.com`,
});

const contentSettings = (title, subtitle) => ({
  default: {
    icon: 'paperPlain',
    subtitle:
      subtitle ||
      'We’ll send them a request asking them to help you complete the required data for this profile.',
    title: title || 'Ask your client to collaborate',
  },
  success: {
    icon: 'round_check',
    subtitle: 'Your Request has been sent',
    title: 'We’ve sent a request to collaborate on this funding search.',
  },
});

const accordionItems = [
  'Your client can help complete the funding application.',
  'Your client can collect and provide the necessary documentation required by lenders.',
  'We’ll update them as the search progresses and offers are received.',
];

const accordionTitle = (
  <>
    <Icon
      className="a-svg-large"
      icon="round_info"
      styleName="o-modal-request-help__accordion-icon"
    />
    What happens when you invite your client?
  </>
);

const RequestHelpModal = (props) => {
  const { data, loading, modalState, openNotify, requestHelp, requestHelpUpdate } = props;

  const [success, setSuccess] = useState(false);
  const clientName = modalState?.getIn(['props', 'clientName']);
  const closeModal = modalState?.getIn(['props', 'closeModal']);
  const formValues = modalState?.getIn(['props', 'formValues']) || {};
  const contentSubtitle = modalState?.getIn(['props', 'subtitle']);
  const contentTitle = modalState?.getIn(['props', 'title']);
  const type = modalState?.getIn(['props', 'requestType'], 'client');
  const fundingSearchId = modalState?.getIn(['props', 'fundingSearchId']);
  const successType = success ? 'success' : 'default';
  const { icon, subtitle, title } = contentSettings(contentTitle, contentSubtitle)?.[successType];
  const { initialValues, schema } = data?.form || {};

  useEffect(() => {
    if (fundingSearchId) {
      requestHelp(fundingSearchId);
    }
  }, [fundingSearchId, requestHelp]);

  const onSubmit = (values) => {
    const valuesWithoutEmptyFields = Object.fromEntries(
      Object.entries(values).filter(([_, v]) => v !== null && v?.length > 0),
    );
    requestHelpUpdate(fundingSearchId, valuesWithoutEmptyFields, type)
      .then(() => setSuccess(true))
      .catch(() =>
        openNotify({
          description: 'Something went wrong.',
          theme: 'error',
          title: 'Try Again',
        }),
      );
  };

  return loading ? (
    <FullPageSpinner withNavbar />
  ) : (
    <div styleName="o-modal-request-help">
      <div className="o-container-m">
        <div className="o-col-block o-col-block--col-collapse">
          <div className="o-col-4 o-col-offset-4">
            <div className="u-align-center">
              <Icon className="a-svg-xxxxxlarge u-color-primary" icon={icon} />
            </div>

            <div styleName="o-modal-request-help__title">{title}</div>

            <div styleName="o-modal-request-help__subtitle">{subtitle}</div>
          </div>

          <div className="o-col-8 o-col-offset-2">
            {success ? (
              <div className="u-align-center">
                <Button
                  className="o-modal__actions__action"
                  label="Return to Profile"
                  onClick={() => closeModal('requestHelpModal')}
                  primary
                />
              </div>
            ) : (
              <FormBuilder
                initialValues={{
                  ...initialValues,
                  ...defaultInitialValues(clientName),
                  ...formValues,
                }}
                noBorder
                noMargin
                noValidate
                onSubmit={onSubmit}
                schema={schema}
                uiSchema={getUiSchema('requestHelp')}
              >
                <Button className={styles['form-builder__actions__button']} primary type="submit">
                  Send Request
                </Button>

                <div styleName="o-modal-request-help__accordion">
                  <Accordion small title={accordionTitle}>
                    {accordionItems.map((item, index) => (
                      <div key={index} styleName="o-modal-request-help__accordion-content">
                        <Icon
                          className="a-svg-large u-color-primary"
                          icon="round_check"
                          styleName="o-modal-request-help__accordion-content-icon"
                        />

                        <span>{item}</span>
                      </div>
                    ))}
                  </Accordion>
                </div>
              </FormBuilder>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

RequestHelpModal.propTypes = {
  data: PropTypes.object,
  loading: PropTypes.bool,
  modalState: PropTypes.instanceOf(Map),
  openNotify: PropTypes.func,
  requestHelp: PropTypes.func.isRequired,
  requestHelpUpdate: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  data: makeSelectRequestHelpResponse(),
  loading: makeSelectRequestHelpLoading(),
});

const mapDispatchToProps = (dispatch) => ({
  requestHelp: (fundingSearchId) => dispatch(requestRequestHelp(fundingSearchId)),
  requestHelpUpdate: (fundingSearchId, payload, type) =>
    new Promise((resolve, reject) =>
      dispatch(
        requestRequestHelpUpdate(fundingSearchId, { ...payload, type }, { resolve, reject }),
      ),
    ),
});

export default compose(
  withModal({
    contentOnTop: false,
    secondary: false,
    modalId: 'requestHelpModal',
    unmountOnClose: true,
  }),
  withNotify,
  connect(mapStateToProps, mapDispatchToProps),
  CSSModules(styles, { allowMultiple: true }),
)(RequestHelpModal);
